import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import type {
  ProviderCustomScriptsSettings,
  UpdateProviderCustomScriptsInput,
} from '@/types/provider-custom-scripts';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const normalizeCustomScripts = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  return value.trim().length > 0 ? value : null;
};

function normalizeSettings(payload: ApiPayload | undefined): ProviderCustomScriptsSettings {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});

  return {
    customScripts: normalizeCustomScripts(
      record?.custom_scripts ?? record?.customScripts,
    ),
  };
}

export class ProviderCustomScriptsRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestProviderCustomScripts(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderCustomScriptsRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderCustomScriptsRequestError('Provider custom scripts request failed', undefined, error);
  }
}

class ProviderCustomScriptsService {
  async getSettings(): Promise<ProviderCustomScriptsSettings> {
    const response = await requestProviderCustomScripts('/api/v1/provider/settings/custom-scripts');
    return normalizeSettings(response);
  }

  async updateSettings(
    input: UpdateProviderCustomScriptsInput,
  ): Promise<ProviderCustomScriptsSettings> {
    const response = await requestProviderCustomScripts('/api/v1/provider/settings/custom-scripts', {
      method: 'PUT',
      body: { custom_scripts: input.customScripts },
    });
    return normalizeSettings(response);
  }
}

export const providerCustomScriptsService = new ProviderCustomScriptsService();

export { normalizeCustomScripts as normalizeProviderCustomScripts };
